import React from "react";
import { SideTab } from "../../../../components/SideTab";
import { TitleLine } from "../../../../components/TitleLine";
import { Button } from "../../../../components/common/Button";
import { Input } from "../../../../components/common/Input";
import { BoxText, Box } from "../../styles";
import MapTracking from "../MapTracking";

interface LocationProps {
  id: string;
  accuracy: string;
  altitude: string;
  latitude: number;
  longitude: number;
}

interface VehicleProps {
  id: string;
  vehicle: string;
  color: string;
  plate: string;
  type: string;
  interface: string;
}

interface BaseProps {
  id: string;
  base: string;
  outpost: string;
}

interface DurationProps {
  text: string;
  value: number;
}

interface OccurrenceProps {
  Occurrence: {
    id: string;
    name: string;
    code: string;
    type: string;
    status: string;
    additional_info: string;
    Base?: BaseProps;
    Location: LocationProps;
    created_at: string;
    updated_at: string;
    OccurrenceReview?: {
      id: string;
      id_user: string;
      id_employee: string;
      id_occurrence: string;
      rating: number;
      additional_info: string;
      created_at: string;
    }[];
  };
  Resident: {
    id_user: string;
    name: string;
    email: string;
    phone: string;
    Vehicle: VehicleProps[];
  };
  Employee?: {
    id: string;
    id_user: string;
    status: string;
    type: string;
    name: string;
    email: string;
    phone: string;
    Vehicle: VehicleProps[];
  };
  Vehicle?: VehicleProps;
  Address?: {
    address: string;
    burgh: string;
    city: string;
    state: string;
    latitude: number;
    longitude: number;
    ResponsibleBase: BaseProps;
  };
  durationLocation: DurationProps;
}

interface SuspectOccurrenceSidebarProps {
  occurrence: OccurrenceProps;
  isOpen: boolean;
  onClose: () => void;
  observations: string;
  onObservationChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onEndOccurrence: () => void;
}

export function SuspectOccurrenceSidebar({
  occurrence,
  isOpen,
  onClose,
  observations,
  onObservationChange,
  onEndOccurrence,
}: SuspectOccurrenceSidebarProps) {
  return (
    <SideTab
      open={isOpen}
      width="40%"
      title={
        "#" +
        occurrence.Occurrence.code +
        " - Ocorrência de " +
        occurrence.Occurrence.type
      }
      toggleFunction={onClose}
    >
      {/* Informações do Segurança */}
      <TitleLine title="Informações do Segurança" />
      
      {/* Nome | Email | Contato (3-2-1 ratio) */}
      <div style={{ display: "flex", gap: "1rem" }}>
        <Input.Root flex="3">
          <Input.Label title="Nome:" htmlFor="occurrenceName" />
          <Input.Wrapper>
            <Input.Content
              id="occurrenceName"
              type="text"
              value={occurrence.Resident.name}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="2">
          <Input.Label title="Email:" htmlFor="emailInfo" />
          <Input.Wrapper>
            <Input.Content
              id="emailInfo"
              type="text"
              value={occurrence.Resident.email || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Contato:" htmlFor="contactInfo" />
          <Input.Wrapper>
            <Input.Content
              id="contactInfo"
              type="text"
              value={occurrence.Resident.phone || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
      </div>

      {/* Endereço | Horário do envio (2-1 ratio) */}
      <div style={{ display: "flex", gap: "1rem" }}>
        <Input.Root flex="2">
          <Input.Label title="Endereço de envio:" htmlFor="occurrenceAddress" />
          <Input.Wrapper>
            <Input.Content
              id="occurrenceAddress"
              type="text"
              value={occurrence.Address ? `${occurrence.Address.address}, ${occurrence.Address.burgh}, ${occurrence.Address.city} - ${occurrence.Address.state}` : "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Horário de envio:" htmlFor="sendTime" />
          <Input.Wrapper>
            <Input.Content
              id="sendTime"
              type="text"
              value={new Date(occurrence.Occurrence.created_at).toLocaleString()}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
      </div>

      {/* Veículo | Cor do carro | Placa */}
      <div style={{ display: "flex", gap: "1rem" }}>
        <Input.Root flex="1">
          <Input.Label title="Veículo:" htmlFor="userVehicle" />
          <Input.Wrapper>
            <Input.Content
              id="userVehicle"
              type="text"
              value={occurrence.Resident.Vehicle?.[0]?.vehicle || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Cor do carro:" htmlFor="vehicleColor" />
          <Input.Wrapper>
            <Input.Content
              id="vehicleColor"
              type="text"
              value={occurrence.Resident.Vehicle?.[0]?.color || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
        <Input.Root flex="1">
          <Input.Label title="Placa:" htmlFor="vehiclePlate" />
          <Input.Wrapper>
            <Input.Content
              id="vehiclePlate"
              type="text"
              value={occurrence.Resident.Vehicle?.[0]?.plate || "-"}
              readOnly
            />
          </Input.Wrapper>
        </Input.Root>
      </div>

      {/* Mapa da ocorrência */}
      {occurrence.Occurrence.status !== "Fechado" && (
        <>
          <TitleLine title="Mapa da ocorrência" />
          <Box padding="0">
            <MapTracking occurrence={occurrence} />
          </Box>
        </>
      )}

      {/* Observations section for suspect occurrences */}
      <TitleLine title="Observações" />
      <BoxText
        padding="0"
        value={observations}
        onChange={onObservationChange}
        placeholder="Digite suas observações aqui..."
      />

      {/* End occurrence button for suspect occurrences */}
      <div style={{ alignSelf: "flex-end", minWidth: "15%", maxWidth: "30%", marginTop: "1rem" }}>
        <Button.Root
          type="button"
          backgroundColor="#0079B1"
          onClick={onEndOccurrence}
        >
          <Button.Content
            title="Finalizar Ocorrência"
            fontSize="12px"
          />
        </Button.Root>
      </div>

      {/* Show Occurrence Review for closed suspect occurrences */}
      {occurrence.Occurrence.status === "Fechado" && (
        <>
          <TitleLine title="Avaliação da Ocorrência" />
          {occurrence.Occurrence.OccurrenceReview &&
          occurrence.Occurrence.OccurrenceReview.length > 0 ? (
            <>
              <div style={{ display: "flex", gap: "1rem" }}>
                <Input.Root flex="1">
                  <Input.Label title="Avaliação:" htmlFor="reviewRating" />
                  <Input.Wrapper>
                    <Input.Content
                      id="reviewRating"
                      type="text"
                      value={`${occurrence.Occurrence.OccurrenceReview?.[0]?.rating || 0}/5`}
                      readOnly
                    />
                  </Input.Wrapper>
                </Input.Root>
                <Input.Root flex="2">
                  <Input.Label title="Data da Avaliação:" htmlFor="reviewDate" />
                  <Input.Wrapper>
                    <Input.Content
                      id="reviewDate"
                      type="text"
                      value={occurrence.Occurrence.OccurrenceReview?.[0]?.created_at ? new Date(
                        occurrence.Occurrence.OccurrenceReview[0].created_at
                      ).toLocaleDateString() : "-"}
                      readOnly
                    />
                  </Input.Wrapper>
                </Input.Root>
              </div>
              <Input.Root>
                <Input.Label title="Comentários:" htmlFor="reviewComments" />
                <Input.Wrapper>
                  <textarea
                    id="reviewComments"
                    value={occurrence.Occurrence.OccurrenceReview?.[0]?.additional_info || ""}
                    readOnly
                    style={{
                      width: "100%",
                      minHeight: "80px",
                      padding: "8px 12px",
                      border: "1px solid #CECECE",
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      resize: "vertical",
                      backgroundColor: "#f9f9f9",
                    }}
                  />
                </Input.Wrapper>
              </Input.Root>
            </>
          ) : (
            <Input.Root>
              <Input.Label title="Status:" htmlFor="noReview" />
              <Input.Wrapper>
                <Input.Content
                  id="noReview"
                  type="text"
                  value="Nenhuma avaliação disponível"
                  readOnly
                />
              </Input.Wrapper>
            </Input.Root>
          )}
        </>
      )}
    </SideTab>
  );
}
